/**
 * Jediný zdroj klíčů pro ``chain-continuations`` dotazy.
 *
 * Endpoint pro pokračování řetězce má tři volající, každý s jiným
 * scope, takže i klíče jsou tři různě dlouhé:
 *
 *   - zákaznický košík        → ``useChainContinuations`` (``queries/processes``)
 *   - zákaznický projekt      → ``useProjectChainContinuations`` (``queries/projects``)
 *   - admin custom-quote draft → ``useCustomQuotePersistence``
 *     (``pages/admin/quotes/adapters/customQuotePersistence``)
 *
 * Všechny tři odpovědi odvozuje BE z tenantova operačního grafu
 * (``get_chain_continuations`` → ``chain_continuation_nodes_for_tenant``),
 * takže uložení Mapy je musí zneplatnit najednou. Prefix match od
 * kořene to neumí — ``"chain-continuations"`` leží v každém klíči jinde.
 * Proto zde žije **segment** a predikát nad ním; invalidace v
 * ``queries/operationGraph`` jede přes ``predicate``, ne přes
 * ``queryKey``.
 */

/** Segment, který nese každý chain-continuations klíč. */
export const CHAIN_CONTINUATIONS_SEGMENT = "chain-continuations";

export const chainContinuationKeys = {
  /** Zákaznický košík — ``GET /cart/items/{slug}/chain-continuations``. */
  cart: (itemSlug: string) =>
    ["cart", CHAIN_CONTINUATIONS_SEGMENT, itemSlug] as const,
  /** Zákaznický projekt — ``GET /projects/{id}/items/{slug}/…``. Leží pod
   *  prefixem ``["project", projectId]``, aby ho zneplatnily i mutace
   *  projektu. */
  project: (projectId: string, itemSlug: string) =>
    ["project", projectId, CHAIN_CONTINUATIONS_SEGMENT, itemSlug] as const,
  /** Admin custom-quote draft — týž endpoint s ``?quote_id=cq_…``. */
  adminQuote: (quoteId: string, itemSlug: string) =>
    ["admin-quote", CHAIN_CONTINUATIONS_SEGMENT, quoteId, itemSlug] as const,
};

/** Sedí klíč na některý z chain-continuations dotazů? */
export function isChainContinuationsQueryKey(key: readonly unknown[]): boolean {
  return key.includes(CHAIN_CONTINUATIONS_SEGMENT);
}
